import { useFirestoreGeneral, useFirestoreLoaded } from '../../firebase/useFirestore';
import MeetstandaardLogo from '../../images/logo-meetstandaard-alt.png'
import { useState } from 'react';
import { client } from '../../hooks/Client';
import { db } from "../../firebase/config";
import { useHistory } from "react-router-dom";
import Location from "../../hooks/Location"
import StakeholderMeta from '../../components/effects/StakeholderMeta';
import ReadOnlyGuard from '../../components/common/ReadOnlyGuard';
import standardEffectAttachStatus from '../../helpers/questionnaire/standardEffectAttachStatus';
import questionnaireBuilderPath from '../../helpers/questionnaire/questionnaireBuilderPath';
import { sentinelToEmpty } from '../../helpers/sentinelValue';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';

// AI-CONTEXT: See docs/features/msi-effect-questionnaire-attach.md
// Een meetstandaard-effect heeft precies één vraagveld dat in hooguit één
// vragenlijst zit. De lijst toont waar het al in zit (en blokkeert het dan),
// en opslaan meldt wanneer er bij een effect niets te koppelen viel — anders
// leest "niets gebeurt" als een willekeurige storing.

/** @typedef {{ id: string, docid: string, effect: string }} CheckedEffect */

/**
 * @param {import('../../helpers/questionnaire/standardEffectAttachStatus').AttachStatus} attach
 * @returns {string} Label onder het effect; '' als er niets te melden is
 */
const attachLabel = ({ status, questionnaireTitle }) => {
    if (status === 'here') return 'In deze vragenlijst'
    if (status === 'elsewhere') return questionnaireTitle ? `In vragenlijst: ${questionnaireTitle}` : 'In een andere vragenlijst'
    return ''
}

const AddStandardEffectsToQuestionaire = () => {
    // State
    const [checkedEffects, setCheckedEffects] = useState(/** @type {CheckedEffect[]} */ ([]))
    const [notAttached, setNotAttached] = useState(/** @type {string[]} */ ([]))
    const [saveError, setSaveError] = useState('')

     // Hooks
     const questionaireId = Location()[3]
     const researchId = sentinelToEmpty(Location()[4])
     const history = useHistory()

    // Firestore
    const standardEffects = useFirestoreGeneral('OutputEffects', 'Standard', 'true')
    const standardFields = useFirestoreGeneral('QuestionnaireFields', 'Standard', 'true')
    const currentFields = useFirestoreGeneral('QuestionnaireFields', 'QuestionnaireID', questionaireId ? questionaireId : '')
    const [questionnaires, questionnairesLoaded] = useFirestoreLoaded('Questionnaires')

    const statusOf = (effect) => standardEffectAttachStatus(effect, {
        fields: standardFields,
        questionnaires,
        questionnairesLoaded,
        currentQuestionnaireId: questionaireId,
    })

    // Update the selected question
    const handleCheckboxChange = (effect) => {
        setCheckedEffects(prevState => {
            const index = prevState.findIndex(item => item.id === effect.ID);
            if (index > -1) {
                // The effect is currently checked, remove it from the array
                return prevState.filter((_, i) => i !== index);
            } else {
                // The effect is not checked, add it to the array
                return [...prevState, { id: effect.ID, docid: effect.docid, effect: effect.Effect }];
            }
        });
    };

    // Get indicators for effect
    // AI-CONTEXT: See docs/pitfalls.md#database-missing-compagnyid-on-fresh-fetch
    const getIndicators = async (effectId) => {
        const indicators = await db.collection('QuestionnaireFields')
        .where('CompagnyID', '==', client)
        .where('EffectId', 'array-contains', effectId)
        .get()

        // Soft-deleted indicators (Deleted: true, ADR-008) stay in Firestore
        // but must not be attached to the questionnaire.
        return indicators.docs.map(doc => ({ ...doc.data(), docid: doc.id })).filter((q) => !q?.Deleted);
    }

    // Save effects to questionaire — one batch, so either every field lands or
    // none does, and nothing navigates before the write is confirmed.
    const updateEffects = async () => {
        if (!Array.isArray(standardFields)) return
        setSaveError('')
        // Achter de bestaande velden: het hoogste Position, niet de telling —
        // na een soft delete lopen die uiteen en zouden posities botsen.
        const positionBase = (Array.isArray(currentFields) ? currentFields : [])
            .reduce((max, f) => Math.max(max, Number(f.Position) || 0), 0)
        const missing = []
        let attached = 0

        try {
            const batch = db.batch()
            for (const item of checkedEffects) {
                const indicators = await getIndicators(item.id)
                if (indicators.length === 0) {
                    missing.push(item.effect)
                    continue
                }
                for (const indicator of indicators) {
                    attached += 1
                    batch.update(db.collection('QuestionnaireFields').doc(indicator.docid), {
                        QuestionnaireID: questionaireId,
                        Position: positionBase + attached,
                    })
                }
            }
            if (attached > 0) await batch.commit()
        } catch (error) {
            console.error(error)
            setSaveError(`De vragenlijst kon niet worden opgeslagen: ${error?.message || error}`)
            return
        }

        if (missing.length > 0) {
            setNotAttached(missing)
            setCheckedEffects(prev => prev.filter(item => missing.includes(item.effect)))
            return
        }

        history.push(questionnaireBuilderPath(client, 'questionairebuilder', questionaireId, researchId))
    }

    const liveEffects = (standardEffects || []).filter((effect) => !effect?.Deleted)

  return (
    <div className="main">
        <div className="main-container" >
            <div className='page-header'>
                <img src={MeetstandaardLogo} alt=''/>
                <h1>Gestandaardiseerde effecten toevoegen</h1>
            </div>
            <ReadOnlyGuard edit="measurement.edit">
            <div className="table-container dashboard-container qb-attach-page">
                <h2>Gestandaardiseerde effecten</h2>
                {liveEffects.length === 0 ? (
                    // Inbox #79: zonder uitleg leest een lege tabel als een laadfout.
                    // De lijst toont de eigen Standard-effecten van de organisatie —
                    // die ontstaan pas na het toevoegen van een meetstandaard-effect.
                    <p className='table-p'>
                        Geen gestandaardiseerde effecten gevonden. Voeg eerst een effect uit de
                        Meetstandaard toe aan jullie Theory of Change (via <strong>Impactstrategie</strong>);
                        daarna kun je het hier aan de vragenlijst koppelen.
                    </p>
                ) : (
                <>
                <table>
                    <thead>
                    <tr>
                        <th>SELECTEER</th>
                        <th>EFFECT</th>
                        <th>DOELGROEP</th>
                    </tr>
                    </thead>
                    <tbody>
                        {liveEffects.map((effect) => {
                            const attach = statusOf(effect)
                            const label = attachLabel(attach)
                            return (
                             <tr key={effect.ID}>
                                <td>
                                    <input
                                    type="checkbox"
                                    checked={checkedEffects.some(q => q.id === effect.ID)}
                                    disabled={attach.status !== 'free'}
                                    onChange={() => handleCheckboxChange(effect)}
                                    />
                                </td>
                                <td>
                                    <p className='table-p'>{effect.Effect}</p>
                                    {label && <span className='qb-attach-status'>{label}</span>}
                                </td>
                                <td>
                                    {effect.Targetgroup?.map((item) => (
                                        <StakeholderMeta key={item} stakeholder={item}/>
                                    ))}
                                </td>
                            </tr>
                            )
                        })}
                    </tbody>
                </table>
                {notAttached.length > 0 && (
                    <p className='table-p qb-attach-warning'>
                        Bij {notAttached.join(', ')} is geen vraag meer gevonden om te koppelen.
                    </p>
                )}
                {saveError && (
                    <p className='table-p qb-attach-warning'>{saveError}</p>
                )}
                {/* Vaste balk onderin, zoals in de bouwer: 'Opslaan' stond als tekstlink
                    onder de vouw en werd niet gevonden (testronde 2026-09-10, B3). */}
                <div className='qb-attach-actionbar'>
                    <button onClick={updateEffects}><AddOutlinedIcon />Opslaan</button>
                </div>
                </>
                )}
            </div>
            </ReadOnlyGuard>


        </div>
    </div>
  )
}

export default AddStandardEffectsToQuestionaire
